import React, { useEffect, useMemo, useState } from "react";
import {
  fetchReports,
  fetchCollections,
  fetchCompletionCounts,
} from "../../api";
import { useAuth0 } from "@auth0/auth0-react";
import { tourStepsReports } from "./tourStepsReports";
import GuidedTour from "../../components/GuidedTour";
import { AxiosError } from "axios";
import BubbleText from "../../components/BubbleText";
import { useTheme } from "../../context/ThemeContext";
import { getAppTextColor, getBackgroundColor } from "../../utils/themeColors";
import { collectionColorSchemes } from "../../constants/colorSchemes";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

/**
 * @fileoverview User performance reporting and analytics component.
 * 
 * @component Reports
 * @description Displays user performance data, session reports, and collection completion statistics.
 * 
 * @typedef {Object} Report
 * @property {number} report_id - Unique identifier for the report
 * @property {number} user_id - ID of user the report belongs to
 * @property {number} total_items - Total items in the session
 * @property {number} time_taken - Session duration in seconds
 * @property {number} missed_items - Number of incorrect attempts
 * @property {number} skipped_items - Number of skipped items
 * @property {string} created_at - Report creation timestamp
 * 
 * @typedef {Object} Collection
 * @property {number} collection_id - Collection identifier
 * @property {string} name - Collection name
 * @property {number} [completion_count] - Times completed
 * 
 * Features:
 * - Session performance reports
 * - Collection completion tracking
 * - Completion charts
 * - Pagination controls
 * - Loading states
 * - Theme-aware styling
 * - Guided tour integration
 */

// Define the Report interface
interface Report {
  report_id: number;
  user_id: number;
  collection_id: number;
  collection_name: string;
  total_items: number;
  time_taken: number; // in seconds
  missed_items: number;
  skipped_items: number;
  created_at: string;
}

// Define the Collection interface
interface Collection {
  collection_id: number;
  name: string;
  completion_count?: number;
}

const truncateLabel = (name: string, maxLength = 18) =>
  name.length > maxLength ? `${name.slice(0, maxLength - 1)}…` : name;

const formatDuration = (seconds: number) => {
  const total = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(total / 60);
  const remainingSeconds = total % 60;
  if (minutes === 0) {
    return `${remainingSeconds}s`;
  }
  return `${minutes}m ${remainingSeconds.toString().padStart(2, "0")}s`;
};

const Reports: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { theme, adjustColorForColorblindness } = useTheme();
  const [reports, setReports] = useState<Report[]>([]);
  const [chartReports, setChartReports] = useState<Report[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [completionCounts, setCompletionCounts] = useState<
    Record<number, number>
  >({});

  const [isTourRunning, setIsTourRunning] = useState<boolean>(false);
  const [currentTourStep, setCurrentTourStep] = useState<number>(0);

  // Define the steps variable without visibility states
  const steps = tourStepsReports(); // Create tour steps without visibility states

  const [page, setPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [limit] = useState<number>(10); // Fixed limit
  const [isNarrowCharts, setIsNarrowCharts] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 639px)");
    const update = () => setIsNarrowCharts(mediaQuery.matches);
    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const loadReports = async () => {
      try {
        setIsLoading(true);
        const data = await fetchReports(getAccessTokenSilently, page, limit);
        setReports(data?.reports ?? []);
        setTotalCount(data?.total_count ?? 0);
      } catch (error) {
        console.error("Failed to load reports", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadReports();
  }, [getAccessTokenSilently, page, limit]);

  // Add pagination controls
  const handleNextPage = () => {
    if (page * limit < totalCount) {
      setPage(page + 1);
    }
  };

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  useEffect(() => {
    const loadCollections = async () => {
      try {
        const fetchedCollections = await fetchCollections(
          getAccessTokenSilently,
        );
        setCollections(fetchedCollections);

        // Fetch completion counts for all collections
        const counts = await fetchCompletionCounts(getAccessTokenSilently);
        setCompletionCounts(counts);
      } catch (error) {
        if (error instanceof AxiosError) {
          console.error(
            "Failed to load collections - Status:",
            error.response?.status,
          );
        } else {
          console.error("Error loading collections");
        }
      }
    };

    loadCollections();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const loadChartReports = async () => {
      try {
        const data = await fetchReports(getAccessTokenSilently, 1, 50);
        setChartReports(data?.reports ?? []);
      } catch (error) {
        console.error("Failed to load chart reports", error);
        setChartReports([]);
      }
    };

    loadChartReports();
  }, [getAccessTokenSilently]);

  // Add a function to start the tour
  const startTour = () => {
    const tourCompleted = localStorage.getItem("tourCompleted");
    if (!tourCompleted) {
      setIsTourRunning(true);
      setCurrentTourStep(0); // Reset to the first step
    }
  };

  // Start the tour when the component mounts
  useEffect(() => {
    startTour(); // Call startTour here
  }, []);

  const handleTourComplete = () => {
    console.log("Tour completed");
    setIsTourRunning(false); // Reset the tour running state
    localStorage.setItem("tourCompleted", "true"); // Mark the tour as completed
  };

  // Update the getCompletionCount function to use actual data
  const getCompletionCount = (collection: Collection): number => {
    return completionCounts[collection.collection_id] || 0;
  };

  const chartTextColor = getAppTextColor(theme);
  const chartGridColor =
    getBackgroundColor(theme).toLowerCase() === "#000000" ? "#444444" : "#DDDDDD";
  const areaColor = adjustColorForColorblindness("#1fc6ff");
  const barChartMargin = {
    top: 8,
    right: 8,
    left: isNarrowCharts ? 8 : 0,
    bottom: isNarrowCharts ? 72 : 32,
  };
  const categoryAxisProps = isNarrowCharts
    ? {
        interval: "preserveStartEnd" as const,
        angle: -45,
        textAnchor: "end" as const,
        height: 72,
        tick: { fill: chartTextColor, fontSize: 11 },
      }
    : {
        interval: 0 as const,
        angle: -30,
        textAnchor: "end" as const,
        height: 60,
        tick: { fill: chartTextColor, fontSize: 12 },
      };

  const completionBarData = useMemo(() => {
    return collections
      .map((collection) => {
        const completions = getCompletionCount(collection);
        return {
          name: collection.name,
          shortName: truncateLabel(collection.name),
          completions,
        };
      })
      .filter((item) => item.completions > 0)
      .sort((a, b) => b.completions - a.completions)
      .slice(0, 8);
  }, [collections, completionCounts]);

  const completionsOverTime = useMemo(() => {
    const countsByDay = new Map<string, number>();
    for (const report of chartReports) {
      const dayKey = new Date(report.created_at).toISOString().slice(0, 10);
      countsByDay.set(dayKey, (countsByDay.get(dayKey) || 0) + 1);
    }
    return [...countsByDay.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([dayKey, completions]) => ({
        date: new Date(`${dayKey}T00:00:00`).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        }),
        completions,
      }));
  }, [chartReports]);

  const totalCompletions = Object.values(completionCounts).reduce(
    (sum, count) => sum + count,
    0,
  );
  const collectionsPracticed = collections.filter(
    (collection) => getCompletionCount(collection) > 0,
  ).length;

  const sessionPerformanceData = useMemo(() => {
    return [...chartReports]
      .slice(0, 10)
      .reverse()
      .map((report, index) => ({
        name: report.collection_name
          ? truncateLabel(report.collection_name, 12)
          : `Session ${index + 1}`,
        fullName: report.collection_name || "Collection",
        missed: report.missed_items,
        skipped: report.skipped_items,
        items: report.total_items,
        time: report.time_taken,
      }));
  }, [chartReports]);

  return (
    <div className={`page-container text-theme-text`}>
      <h1 className="inherit mb-8 text-3xl font-bold">
        <BubbleText>User Performance Reports</BubbleText>
      </h1>
      <p className="inherit">
        After each session, performance is saved here: items shown, time taken,
        missed items, and skipped items. Use this to monitor student progress.
      </p>

      {isLoading ? (
        <p className="inherit">Loading reports...</p>
      ) : (
        <div className="reports-overview">
          <h2 className="inherit mb-4 text-2xl font-semibold">
            Reports Overview
          </h2>

          <div className="reports-charts mb-8 grid gap-4 sm:grid-cols-3">
            <div className="inherit rounded border p-4 text-center">
              <p className="text-sm opacity-75">Total completions</p>
              <p className="text-3xl font-bold">{totalCompletions}</p>
            </div>
            <div className="inherit rounded border p-4 text-center">
              <p className="text-sm opacity-75">Collections practiced</p>
              <p className="text-3xl font-bold">{collectionsPracticed}</p>
            </div>
            <div className="inherit rounded border p-4 text-center">
              <p className="text-sm opacity-75">Recent sessions</p>
              <p className="text-3xl font-bold">{chartReports.length}</p>
            </div>
          </div>

          <div className="mb-8 grid gap-6 lg:grid-cols-2">
            <div className="inherit rounded border p-4">
              <h3 className="inherit mb-4 text-xl font-semibold">
                Completions by collection
              </h3>
              {completionBarData.length === 0 ? (
                <p className="inherit py-12 text-center text-sm">
                  Complete a collection to see this chart.
                </p>
              ) : (
                <div className="h-72 w-full overflow-x-auto">
                  <ResponsiveContainer width="100%" height="100%" minWidth={280}>
                    <BarChart
                      data={completionBarData}
                      margin={barChartMargin}
                    >
                      <CartesianGrid stroke={chartGridColor} strokeDasharray="3 3" />
                      <XAxis dataKey="shortName" {...categoryAxisProps} />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fill: chartTextColor, fontSize: 12 }}
                      />
                      <Tooltip
                        formatter={(value: number) => [value, "Completions"]}
                        labelFormatter={(_, payload) =>
                          payload?.[0]?.payload?.name ?? ""
                        }
                      />
                      <Bar dataKey="completions" radius={[6, 6, 0, 0]}>
                        {completionBarData.map((entry, index) => (
                          <Cell
                            key={entry.name}
                            fill={adjustColorForColorblindness(
                              collectionColorSchemes[
                                index % collectionColorSchemes.length
                              ].backgroundColor,
                            )}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="inherit rounded border p-4">
              <h3 className="inherit mb-4 text-xl font-semibold">
                Completions over time
              </h3>
              {completionsOverTime.length === 0 ? (
                <p className="inherit py-12 text-center text-sm">
                  Complete a collection to see this chart.
                </p>
              ) : (
                <div className="h-72 w-full overflow-x-auto">
                  <ResponsiveContainer width="100%" height="100%" minWidth={280}>
                    <AreaChart
                      data={completionsOverTime}
                      margin={{ top: 8, right: 8, left: isNarrowCharts ? 8 : 0, bottom: 8 }}
                    >
                      <CartesianGrid stroke={chartGridColor} strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tick={{ fill: chartTextColor, fontSize: 12 }}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fill: chartTextColor, fontSize: 12 }}
                      />
                      <Tooltip
                        formatter={(value: number) => [value, "Completions"]}
                      />
                      <Area
                        type="monotone"
                        dataKey="completions"
                        stroke={areaColor}
                        fill={areaColor}
                        fillOpacity={0.35}
                        strokeWidth={3}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          <div className="inherit mb-8 rounded border p-4">
            <h3 className="inherit mb-4 text-xl font-semibold">
              Missed and skipped items
            </h3>
            {sessionPerformanceData.length === 0 ? (
              <p className="inherit py-12 text-center text-sm">
                Complete a session to see missed and skipped items.
              </p>
            ) : (
              <div className="h-72 w-full overflow-x-auto">
                <ResponsiveContainer width="100%" height="100%" minWidth={280}>
                  <BarChart
                    data={sessionPerformanceData}
                    margin={barChartMargin}
                  >
                    <CartesianGrid stroke={chartGridColor} strokeDasharray="3 3" />
                    <XAxis dataKey="name" {...categoryAxisProps} />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fill: chartTextColor, fontSize: 12 }}
                    />
                    <Tooltip
                      labelFormatter={(_, payload) =>
                        payload?.[0]?.payload?.fullName ?? ""
                      }
                    />
                    <Legend wrapperStyle={{ fontSize: isNarrowCharts ? 12 : 14 }} />
                    <Bar
                      dataKey="missed"
                      name="Missed"
                      fill={adjustColorForColorblindness("#ef4444")}
                      radius={[6, 6, 0, 0]}
                    />
                    <Bar
                      dataKey="skipped"
                      name="Skipped"
                      fill={adjustColorForColorblindness("#eab308")}
                      radius={[6, 6, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <ul className="session-reports-list space-y-4">
            {reports.length === 0 ? (
              <li className="inherit rounded border p-4">
                No session reports yet. Finish a collection session to see
                items shown, time taken, missed items, and skipped items.
              </li>
            ) : (
              reports.map((report) => (
                <li key={report.report_id} className="inherit rounded border p-4">
                  <p className="inherit mb-2 text-lg font-semibold">
                    {report.collection_name || "Collection"}
                  </p>
                  <p className="inherit">Items shown: {report.total_items}</p>
                  <p className="inherit">
                    Time taken: {formatDuration(report.time_taken)}
                  </p>
                  <p className="inherit">Missed items: {report.missed_items}</p>
                  <p className="inherit">Skipped items: {report.skipped_items}</p>
                  <p className="inherit text-sm opacity-75">
                    {new Date(report.created_at).toLocaleString()}
                  </p>
                </li>
              ))
            )}
          </ul>

          <h2 className="inherit mb-4 mt-8 text-2xl font-semibold">
            Your Collections
          </h2>
          <ul className="space-y-2">
            {collections.map((collection) => (
              <li key={collection.collection_id} className="inherit">
                {collection.name} - Completed: {getCompletionCount(collection)}{" "}
                times
              </li>
            ))}
          </ul>
        </div>
      )}
      <GuidedTour
        steps={steps}
        isRunning={isTourRunning}
        onComplete={handleTourComplete}
        currentStep={currentTourStep}
        onStepChange={(step) => setCurrentTourStep(step)}
        tourName="reports"
      />
      {/* Pagination controls */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={handlePrevPage}
          disabled={page === 1}
          className="min-h-[44px] rounded bg-blue-500 px-4 py-2 text-white disabled:opacity-50"
        >
          Previous
        </button>
        <span>
          Page {page} of {Math.ceil(totalCount / limit)}
        </span>
        <button
          type="button"
          onClick={handleNextPage}
          disabled={page * limit >= totalCount}
          className="min-h-[44px] rounded bg-blue-500 px-4 py-2 text-white disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Reports;
